import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Spinner, Table } from "react-bootstrap";
import { Card, Row, Col } from "@themesberg/react-bootstrap";
import { toast } from "react-hot-toast";
import { postApi } from "../../../redux/api";
import { useSelector } from "react-redux";
import Scrollbar from "../../sub-treasury/cash-backing/ScrollBar";
import "../../main-treasury/payment-schedule/table.css";
import { Routes } from "../../../routes";
import BackButton from "../../../components/BackButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { formatNumber } from "../../../redux/utils";
import { useResponsive } from "../../../hooks/useResponsive";
import ResponsiveContainer from "../../../components/ResponsiveContainer";
import MobileDataCard from "../../../components/MobileDataCard";

function NewScheduleList() {
  const { isMobile } = useResponsive();
  const user = useSelector((state) => state.auth.user);
  const isSelfSustained = user?.isSelfSustained;
  const history = useNavigate();
  const [pvInfo, setPvInfo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());

  const toggleSelectItem = (pv_code) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(pv_code)) next.delete(pv_code);
      else next.add(pv_code);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === pvInfo.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(pvInfo.map((i) => i.pv_code)));
    }
  };

  const fetchData = () => {
    setLoading(true);
    const pvStatus = (isSelfSustained === 'true' || isSelfSustained === true)
      ? 'SA_approved'
      : 'DAG Approved';

    postApi(`get_pv_collection`, {
      form: { query_type: "pv_by_approval_for_schedule", pv_status: pvStatus },
    })
      .then((data) => {
        if (data.result && data.result.length) {
          setPvInfo(data.result);
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [isSelfSustained]);

  const handleApprove = () => {
    if (selectedItems.size === 0) {
      toast.error("Please select at least one item to create a schedule");
      return;
    }

    setSubmitting(true);

    const queryType = (isSelfSustained === 'true' || isSelfSustained === true)
      ? 'sa_insert'
      : 'insert';

    const selectedData = pvInfo.filter((i) => selectedItems.has(i.pv_code));

    postApi(`post_contractor_schedule_array`, {
      contractScheduleTable: selectedData,
      query_type: queryType,
      notif_key: "Review Payment Schedule",
      mda_name: "Office of the Accountant-General",
      approval_no: selectedData[0]?.approval_no,
    })
      .then((data) => {
        if (data.success) {
          toast.success("Payment Initiated Successfully!");
          setPvInfo([]);
          setSelectedItems(new Set());
          fetchData();
        }
      })
      .catch((err) => {
        toast.error(err);
      })
      .finally(() => setSubmitting(false));
  };

  const totalAmount = pvInfo.reduce((sum, item) => {
    return sum + parseFloat(item.amount || 0);
  }, 0);

  return (
    <>
      <BackButton />
      <Card className="my-2">
        <Card.Header>
          <h5>Create Payment Schedule</h5>
        </Card.Header>

        <Card.Body>
          {loading && <Spinner />}

          <div className={isMobile ? "mobile-table-container" : ""}>
            {isMobile ? (
              <div className="mobile-cards-container">
                {pvInfo && pvInfo.map((item, index) => (
                  <div key={index} className="mb-2 p-2 border rounded position-relative">
                    <div className="mb-2">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.pv_code)}
                        onChange={() => toggleSelectItem(item.pv_code)}
                        className="me-2"
                        style={{ width: 18, height: 18, cursor: "pointer" }}
                      />
                      <strong>Select</strong>
                    </div>
                    <MobileDataCard
                      title={`PV ${item.pv_code}`}
                      data={item}
                      primaryField={{
                        label: "Amount",
                        value: item.amount,
                        type: "currency"
                      }}
                      secondaryFields={[
                        { label: "MDA Name", value: item.mda_name },
                        { label: "Admin Code", value: item.mda_code },
                        { label: "Economic/Program Code", value: item.economic_code || item.program_code },
                        {
                          label: "Payee Account",
                          value: item.contractor_acc_name && item.contractor_acc_no
                            ? `${item.contractor_acc_name} - ${item.contractor_acc_no}`
                            : "Account details not available"
                        }
                      ]}
                      actions={[
                        { type: "view_pv", label: "View PV", icon: faEye, variant: "primary" }
                      ]}
                      onActionClick={(actionType, data) => {
                        if (actionType === "view_pv") {
                          history(
                            `${Routes.pv.pvReportView}/${item.pv_code}?approval_no=${encodeURIComponent(data.approval_no)}&approval_type=${encodeURIComponent(data.approval_type)}`
                          );
                        }
                      }}
                    />
                  </div>
                ))}
                <div className="mobile-total-card mt-3">
                  <Card className="bg-primary text-white">
                    <Card.Body className="text-center">
                      <h5 className="mb-0">Total: ₦{formatNumber(totalAmount)}</h5>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            ) : (
              <Scrollbar height={"50vh"}>
                <table id="customers">
                  <thead>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={pvInfo.length > 0 && selectedItems.size === pvInfo.length}
                        onChange={toggleSelectAll}
                        style={{ width: 18, height: 18, cursor: "pointer" }}
                      />
                    </th>
                    <th style={{ width: "5%" }}>PV Code</th>
                    <th style={{ width: "5%" }}>Approval No</th>
                    <th style={{ width: "10%" }}>Mda Name</th>
                    <th style={{ width: "10%" }}>Admin Code</th>
                    <th style={{ width: "10%" }}>Economic/Program code</th>
                    <th style={{ width: "10%" }}>Payee Account Details</th>
                    <th>Amount</th>
                    <th style={{ width: "10%" }}>Action</th>
                  </thead>

                  <tbody>
                    {pvInfo && pvInfo.map((item, index) => (
                      <tr key={index} style={{ backgroundColor: selectedItems.has(item.pv_code) ? "#d4edda" : "" }}>
                        <td style={{ width: 40, textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={selectedItems.has(item.pv_code)}
                            onChange={() => toggleSelectItem(item.pv_code)}
                            style={{ width: 18, height: 18, cursor: "pointer" }}
                          />
                        </td>
                        <td style={{ width: "5%" }}>{item.pv_code}</td>
                        <td style={{ width: "5%" }}>{item.approval_no}</td>
                        <td style={{ width: "10%" }}>
                          <div style={{ width: 150, overflow: "inherit" }}>
                            {item.mda_name}
                          </div>
                        </td>
                        <td style={{ width: "10%" }}>{item.mda_code}</td>
                        <td style={{ width: "10%" }}>{item.economic_code || item.program_code}</td>
                        <td style={{ width: "10%" }}>
                          <div><b>Bank Name:</b> {item.contractor_bank_name}</div>
                          <div><b>Account Name:</b> {item.contractor_acc_name}</div>
                          <div><b>Account No.:</b> {item.contractor_acc_no}</div>
                          <div><b>Account Type:</b> {item.account_type}</div>
                          <div><b>Sort Code:</b> {item.sort_code}</div>
                        </td>
                        <td style={{ textAlign: "right", width: "10%" }}>
                          {formatNumber(item?.amount)}
                        </td>
                        <td>
                          <Button
                            onClick={() => {
                              history(
                                `${Routes.pv.pvReportView}/${item.pv_code}?approval_no=${encodeURIComponent(item.approval_no)}&approval_type=${encodeURIComponent(item.approval_type)}`
                              );
                            }}
                          >
                            PV
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="table-total mt-3 text-end">
                  <h5><b>Total: ₦{formatNumber(totalAmount)}</b></h5>
                </div>
              </Scrollbar>
            )}
          </div>

          <center className="mt-3">
            <Button onClick={handleApprove}>
              {submitting ? (
                <>
                  <Spinner /> Please wait..
                </>
              ) : (
                "Create Schedule"
              )}
            </Button>
          </center>
        </Card.Body>
      </Card>
    </>
  );
}

export default NewScheduleList;
